import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

const useValidateAddress = (geocoder, form, setCenterLatLng, setMeetingLatLng, setIsButtonDisabled, setButtonLabel) => {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation()

  const match = (component, value) => {
    if (!component || value == null) return false
    const valueLower = String(value).toLowerCase().trim()
    const longName = (component.long_name || "").toLowerCase().trim()
    const shortName = (component.short_name || "").toLowerCase().trim()
    return longName === valueLower || shortName === valueLower
  }

  const getComponent = (addressComponents, types) => {
    const component = addressComponents.find((comp) => types.some((t) => comp.types.includes(t)))
    return component ? { long_name: component.long_name, short_name: component.short_name } : null
  }

  const validateAddressComponents = useCallback((place, values) => {
    const { street_address = "", city = "", state = "", zip_code = "", country = "" } = values
    const addressComponents = place.address_components ?? []
    const streetAddressLower = String(street_address || "")
      .toLowerCase()
      .trim()
    const formattedAddressLower = String(place.formatted_address || "").toLowerCase()

    const stateComponent = getComponent(addressComponents, ["administrative_area_level_1"])
    const countryComponent = getComponent(addressComponents, ["country"])
    const cityComponent = getComponent(addressComponents, [
      "locality",
      "sublocality",
      "administrative_area_level_1",
      "administrative_area_level_2"
    ])
    const zipComponent = getComponent(addressComponents, ["postal_code"])

    return {
      isStreetAddress: streetAddressLower ? formattedAddressLower.includes(streetAddressLower) : false,
      isZipValid: match(zipComponent, zip_code),
      isCityValid: match(cityComponent, city),
      isStateValid: match(stateComponent, state),
      isCountryValid: match(countryComponent, country)
    }
  }, [])

  const handleValidationSuccess = useCallback(
    (place) => {
      const location = place.geometry.location
      const formatted_address = place.formatted_address ?? ""
      setMeetingLatLng(formatted_address)
      setCenterLatLng({ lat: location.lat(), lng: location.lng() })
      setIsButtonDisabled(true)
    },
    [setCenterLatLng, setMeetingLatLng, setIsButtonDisabled]
  )

  const handleValidationFailure = useCallback(
    (validationResults, form) => {
      setCenterLatLng?.(null)
      setMeetingLatLng(null)
      setButtonLabel(t("listings.tabs.location.confirm_address"))
      const errors = [
        !validationResults.isStreetAddress && {
          name: "street_address",
          errors: [t("listings.tabs.location.validation.invalid_street_address")]
        },
        !validationResults.isZipValid && { name: "zip_code", errors: [t("listings.tabs.location.validation.invalid_zip_code")] },
        !validationResults.isCityValid && { name: "city", errors: [t("listings.tabs.location.validation.invalid_city")] },
        !validationResults.isStateValid && { name: "state", errors: [t("listings.tabs.location.validation.invalid_state")] },
        !validationResults.isCountryValid && { name: "country", errors: [t("listings.tabs.location.validation.invalid_country")] }
      ].filter(Boolean)
      form.setFields(errors)
    },
    [setCenterLatLng, setMeetingLatLng, t]
  )

  const handleConfirmAddress = useCallback(async () => {
    setLoading(true)
    try {
      const values = await form.validateFields()
      const userAddress = Object.values(values).join(", ")

      const result = await Promise.race([
        new Promise((resolve) => {
          try {
            geocoder({ address: userAddress }, (place) => {
              try {
                if (place) {
                  const validationResults = validateAddressComponents(place, values)
                  const isValid = Object.values(validationResults).every(Boolean)
                  if (isValid) {
                    handleValidationSuccess(place)
                    resolve(true)
                  } else {
                    handleValidationFailure(validationResults, form)
                    resolve(false)
                  }
                } else {
                  handleValidationFailure({ isStreetAddress: false }, form)
                  resolve(false)
                }
              } catch (innerErr) {
                console.error(innerErr)
                handleValidationFailure({ isStreetAddress: false }, form)
                resolve(false)
              }
            })
          } catch (outerErr) {
            console.error(outerErr)
            handleValidationFailure({ isStreetAddress: false }, form)
            resolve(false)
          }
        }),
        // Fallback timeout: ensure we never hang if callback never fires
        new Promise((resolve) => setTimeout(() => resolve(false), 10000))
      ])
      return result
    } catch (err) {
      console.error(err)
      return false
    } finally {
      setTimeout(() => {
        setLoading(false)
      }, 1000)
    }
  }, [form, geocoder, validateAddressComponents, handleValidationSuccess, handleValidationFailure])

  return { handleConfirmAddress, loading }
}

export default useValidateAddress
